import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, BadgeCheck, MapPin, Clock, AlertTriangle, ShieldCheck, MessageSquare, Video, Globe2, Users2, Award, Sparkles, PlayCircle } from "lucide-react";
import { coachlyApi } from "@/lib/api";
import { StarsDisplay } from "@/components/coachly/StarRating";
import ReviewForm from "@/components/coachly/ReviewForm";
import ReviewItem from "@/components/coachly/ReviewItem";
import BookingWidget from "@/components/coachly/BookingWidget";
import MarketplaceNav from "@/components/coachly/MarketplaceNav";
import { TrustRow } from "@/components/coachly/TrustBadges";
import CoachBadges from "@/components/coachly/CoachBadges";
import ReportCoachDialog from "@/components/coachly/ReportCoachDialog";
import SupportLink from "@/components/coachly/SupportLink";
import ChatDrawer from "@/components/coachly/ChatDrawer";
import SaveCoachButton from "@/components/coachly/SaveCoachButton";
import MediaLightbox from "@/components/coachly/MediaLightbox";
import BundleCards from "@/components/coachly/BundleCards";
import { useAuth } from "@/context/AuthContext";
import { WEEKDAY_SHORT, tzOffsetLabel } from "@/lib/sessionFormat";
import { COACH_PROFILE } from "@/constants/testIds";

export default function CoachProfile() {
  const { slug } = useParams();
  const [coach, setCoach] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [media, setMedia] = useState([]);
  const [bundles, setBundles] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reportOpen, setReportOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const { user, openSignIn } = useAuth();
  const requireChat = () => {
    if (!user) return openSignIn(`Sign in to message ${coach?.name?.split(" ")[0] || "your coach"} privately.`);
    setChatOpen(true);
  };

  const load = async () => {
    setLoading(true);
    try {
      const res = await coachlyApi.getCoach(slug);
      setCoach(res.coach);
      setReviews(res.reviews || []);
      setMedia(res.media || []);
      setBundles(res.bundles || []);
      setError(null);
    } catch (err) {
      setError(err?.response?.data?.detail || "Coach not found");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [slug]);

  if (loading) {
    return (
      <div className="bg-white min-h-screen">
        <MarketplaceNav />
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-16 animate-pulse">
          <div className="h-72 rounded-2xl bg-slate-100" />
          <div className="mt-8 h-8 w-1/3 bg-slate-100 rounded" />
          <div className="mt-4 h-4 w-1/2 bg-slate-100 rounded" />
        </div>
      </div>
    );
  }

  if (error || !coach) {
    return (
      <div className="bg-white min-h-screen">
        <MarketplaceNav />
        <div className="max-w-[1000px] mx-auto px-6 md:px-10 py-24 text-center">
          <div className="eyebrow">§ 404</div>
          <h1 className="mt-4 font-display text-4xl tracking-tight text-slate-900">Coach not found.</h1>
          <p className="mt-3 text-slate-500">{error}</p>
          <Link to="/marketplace" className="mt-8 inline-block h-11 px-5 rounded-full bg-slate-900 text-white text-[13px] font-semibold hover:bg-[#2563EB] transition-colors">
            Back to marketplace
          </Link>
        </div>
      </div>
    );
  }

  const hasReviews = reviews.length > 0;

  return (
    <div data-testid={COACH_PROFILE.page} className="bg-white min-h-screen">
      <MarketplaceNav />

      {/* Cover */}
      <section className="relative h-[280px] md:h-[380px] overflow-hidden bg-slate-100">
        <img src={coach.cover_url} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-transparent to-white" />
      </section>

      <section className="relative -mt-24 md:-mt-32 max-w-[1200px] mx-auto px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          data-testid={COACH_PROFILE.header}
          className="bg-white border border-slate-200 rounded-3xl p-6 md:p-10 shadow-[0_30px_80px_-40px_rgba(15,23,42,0.15)]"
        >
          <Link to="/marketplace" className="inline-flex items-center gap-1.5 text-[12.5px] text-slate-500 hover:text-slate-900 transition-colors">
            <ArrowLeft className="w-3.5 h-3.5" strokeWidth={2} /> Back to marketplace
          </Link>

          <div className="mt-6 grid md:grid-cols-[auto_1fr_auto] gap-6 md:gap-10 items-start">
            <img
              src={coach.avatar_url}
              alt={coach.name}
              className="w-24 h-24 md:w-32 md:h-32 rounded-2xl object-cover ring-1 ring-slate-200"
            />
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="font-display font-medium text-3xl md:text-5xl tracking-tight text-slate-900">
                  {coach.name}
                </h1>
                {coach.is_verified && (
                  <span className="inline-flex items-center gap-1 text-[12px] font-medium text-[#2563EB] bg-[#2563EB]/8 border border-[#2563EB]/20 rounded-full px-2.5 py-1">
                    <BadgeCheck className="w-3.5 h-3.5" strokeWidth={2.4} />
                    Verified coach
                  </span>
                )}
              </div>
              <p className="mt-3 text-slate-600 text-[15px] md:text-[17px] max-w-[70ch] leading-relaxed">
                {coach.headline}
              </p>
              <div className="mt-5 flex items-center gap-5 flex-wrap text-[13px] text-slate-500">
                <span className="inline-flex items-center gap-1.5">
                  <MapPin className="w-3.5 h-3.5" strokeWidth={2} /> {coach.city}
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <Clock className="w-3.5 h-3.5" strokeWidth={2} /> {coach.years_experience}+ yrs experience
                </span>
                <span>Speaks {coach.languages.join(", ")}</span>
              </div>

              <div className="mt-5 flex items-center gap-3">
                <StarsDisplay value={coach.avg_rating} size={16} />
                <span data-testid={COACH_PROFILE.avgRating} className="text-[15px] font-semibold text-slate-900">
                  {coach.review_count > 0 ? coach.avg_rating.toFixed(1) : "New"}
                </span>
                <span data-testid={COACH_PROFILE.reviewCount} className="text-[13.5px] text-slate-500">
                  ({coach.review_count} review{coach.review_count === 1 ? "" : "s"})
                </span>
              </div>

              <div className="mt-6 flex flex-wrap gap-1.5">
                {coach.specialties.map((s) => (
                  <span key={s} className="text-[11px] tracking-wide uppercase font-medium px-2.5 py-1 rounded-full bg-slate-100 text-slate-600">
                    {s}
                  </span>
                ))}
              </div>

              <div className="mt-5">
                <TrustRow coach={coach} testId={`coach-profile-trust-${coach.slug}`} />
              </div>
            </div>

            <div className="md:text-right">
              <div className="text-[11px] tracking-[0.24em] uppercase text-slate-500 font-semibold">Rate</div>
              <div className="mt-2 font-display font-medium text-4xl tracking-tight text-slate-900">
                ${coach.price_per_session ?? coach.hourly_rate}
                <span className="text-lg text-slate-500 font-body">/ {coach.session_duration_minutes || 60} min</span>
              </div>
              <Link
                to={`#booking-widget`}
                data-testid={COACH_PROFILE.bookCta}
                onClick={(e) => {
                  e.preventDefault();
                  const el = document.getElementById("booking-widget");
                  if (el && window.__lenis?.scrollTo) window.__lenis.scrollTo(el, { offset: -80 });
                  else el?.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
                className="mt-4 inline-flex btn-primary h-12 px-6 rounded-full text-[13.5px] font-semibold items-center gap-2"
              >
                Book a session
                <span aria-hidden>→</span>
              </Link>
              <button
                type="button"
                onClick={requireChat}
                data-testid="coach-message-btn"
                className="mt-3 md:ml-3 inline-flex items-center gap-2 h-12 px-5 rounded-full bg-white border border-slate-200 text-slate-900 hover:border-[#2563EB] hover:text-[#2563EB] text-[13.5px] font-semibold transition-colors"
              >
                <MessageSquare className="w-4 h-4" strokeWidth={2.2} />
                Message coach
              </button>
              <SaveCoachButton coachSlug={coach.slug} variant="pill" className="mt-3 md:ml-3" />
            </div>
          </div>

          {/* Trust + rating badges — surfaced high so clients see them
              without scrolling. */}
          {coach.badges && coach.badges.length > 0 && (
            <div className="mt-6">
              <CoachBadges badges={coach.badges} variant="profile" />
            </div>
          )}

          <div className="mt-10 pt-8 border-t border-slate-100 max-w-[70ch]">
            <div className="eyebrow">§ About</div>
            <p className="mt-3 text-slate-700 text-[15px] leading-relaxed whitespace-pre-wrap">
              {coach.bio}
            </p>
          </div>

          {/* Why book with me */}
          {coach.why_book_with_me && (
            <div className="mt-10 pt-8 border-t border-slate-100 max-w-[70ch]" data-testid="coach-why-book">
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-[#2563EB]" strokeWidth={2.4} />
                <div className="eyebrow">§ Why book with {coach.name.split(" ")[0]}</div>
              </div>
              <p className="mt-3 text-slate-700 text-[15px] leading-relaxed">
                {coach.why_book_with_me}
              </p>
            </div>
          )}

          {/* Achievements */}
          {(coach.achievements || []).length > 0 && (
            <div className="mt-10 pt-8 border-t border-slate-100 max-w-[70ch]" data-testid="coach-achievements">
              <div className="flex items-center gap-2">
                <Award className="w-4 h-4 text-[#2563EB]" strokeWidth={2.4} />
                <div className="eyebrow">§ Achievements</div>
              </div>
              <ul className="mt-4 space-y-2.5">
                {coach.achievements.map((a, i) => (
                  <li key={i} className="flex items-start gap-3 text-[14px] text-slate-700 leading-relaxed">
                    <span className="mt-1 w-1.5 h-1.5 rounded-full bg-[#2563EB] shrink-0" />
                    <span>{a}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Certifications strip */}
          {(coach.certifications || []).length > 0 && (
            <div className="mt-10 pt-8 border-t border-slate-100 max-w-[70ch]" data-testid="coach-certifications">
              <div className="eyebrow">§ Certifications</div>
              <div className="mt-3 flex flex-wrap gap-2">
                {coach.certifications.map((c, i) => (
                  <span key={i} className="inline-flex items-center h-8 px-3 rounded-full bg-[#2563EB]/8 text-[#2563EB] text-[12.5px] font-semibold">
                    {c}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Target audience */}
          {(coach.target_audience || []).length > 0 && (
            <div className="mt-10 pt-8 border-t border-slate-100 max-w-[70ch]" data-testid="coach-target-audience">
              <div className="flex items-center gap-2">
                <Users2 className="w-4 h-4 text-[#2563EB]" strokeWidth={2.4} />
                <div className="eyebrow">§ Who I train</div>
              </div>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {coach.target_audience.map((t, i) => (
                  <span key={i} className="inline-flex items-center h-7 px-3 rounded-full bg-slate-100 text-slate-700 text-[12px] font-medium capitalize">
                    {t}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Intro video */}
          {coach.intro_video_url && (
            <div className="mt-10 pt-8 border-t border-slate-100 max-w-[70ch]" data-testid="coach-intro-video">
              <div className="flex items-center gap-2">
                <PlayCircle className="w-4 h-4 text-[#2563EB]" strokeWidth={2.4} />
                <div className="eyebrow">§ Intro video</div>
              </div>
              <div className="mt-4 aspect-video rounded-2xl overflow-hidden bg-slate-900">
                <iframe
                  src={_embedUrl(coach.intro_video_url)}
                  title={`Intro video for ${coach.name}`}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="w-full h-full"
                />
              </div>
            </div>
          )}

          {/* Session at a glance */}
          <SessionAtAGlance coach={coach} />

          {/* Cancellation policy */}
          <div className="mt-10 pt-8 border-t border-slate-100 max-w-[70ch]" data-testid="coach-cancellation-policy">
            <div className="flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-[#2563EB]" strokeWidth={2.4} />
              <div className="eyebrow">§ Cancellation policy</div>
            </div>
            <p className="mt-3 text-slate-600 text-[14px] leading-relaxed">
              {coach.cancellation_policy}
            </p>
          </div>

          {/* Trust actions */}
          <div className="mt-10 pt-6 border-t border-slate-100 flex flex-wrap items-center gap-4 justify-between">
            <SupportLink compact />
            <button
              type="button"
              onClick={() => setReportOpen(true)}
              data-testid="coach-report-btn"
              className="inline-flex items-center gap-1.5 text-[12.5px] font-medium text-slate-500 hover:text-red-600 transition-colors"
            >
              <AlertTriangle className="w-3.5 h-3.5" strokeWidth={2} />
              Report {coach.name.split(" ")[0]}
            </button>
          </div>
        </motion.div>
      </section>

      <ReportCoachDialog
        coachSlug={coach.slug}
        coachName={coach.name}
        open={reportOpen}
        onClose={() => setReportOpen(false)}
      />

      <ChatDrawer
        coachSlug={coach.slug}
        coachName={coach.name}
        open={chatOpen}
        onClose={() => setChatOpen(false)}
      />

      {/* Session bundles — optional; nothing rendered when the coach has none */}
      <BundleCards
        bundles={bundles}
        coachName={coach.name}
        coachSlug={coach.slug}
      />

      {/* Portfolio — coach-curated photos and short videos, drafts hidden */}
      {media.length > 0 && (
        <section className="max-w-[1200px] mx-auto px-6 md:px-10 py-12 md:py-16" data-testid="coach-portfolio">
          <div className="eyebrow">§ Portfolio</div>
          <h2 className="mt-3 font-display text-3xl md:text-4xl tracking-tight text-slate-900">
            See {coach.name.split(" ")[0]} in action
          </h2>
          <p className="mt-2 text-[13.5px] text-slate-500">Click any tile to expand · use ← / → to navigate</p>
          <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {media.map((m, idx) => (
              <li
                key={m.id}
                data-testid={`portfolio-item-${m.id}`}
                className="rounded-2xl overflow-hidden border border-slate-200 bg-slate-50 group"
              >
                <button
                  type="button"
                  onClick={() => setLightboxIndex(idx)}
                  data-testid={`portfolio-open-${m.id}`}
                  className="block w-full text-left"
                  aria-label={m.caption ? `Open ${m.caption} in fullscreen` : "Open media in fullscreen"}
                >
                  <div className="relative aspect-video bg-slate-100 overflow-hidden">
                    {m.type === "video" ? (
                      <video
                        src={m.url}
                        preload="metadata"
                        muted
                        className="w-full h-full object-cover bg-black"
                      />
                    ) : (
                      <img
                        src={m.url}
                        alt={m.caption || `${coach.name} portfolio`}
                        loading="lazy"
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                      />
                    )}
                    <span className="absolute inset-0 bg-black/0 group-hover:bg-black/25 transition-colors duration-300" />
                  </div>
                </button>
                {m.caption ? (
                  <p className="px-4 py-3 text-[13.5px] text-slate-600 leading-relaxed">
                    {m.caption}
                  </p>
                ) : null}
              </li>
            ))}
          </ul>
          <MediaLightbox
            items={media}
            index={lightboxIndex}
            onIndexChange={setLightboxIndex}
            onClose={() => setLightboxIndex(null)}
          />
        </section>
      )}

      {/* Reviews */}
      <section className="max-w-[1200px] mx-auto px-6 md:px-10 py-16 md:py-24">
        <div className="grid lg:grid-cols-[1fr_360px] gap-10">
          <div>
            <div className="flex items-baseline justify-between gap-4 flex-wrap">
              <div>
                <div className="eyebrow">§ Reviews</div>
                <h2 className="mt-3 font-display text-3xl md:text-4xl tracking-tight text-slate-900">
                  What clients say
                </h2>
              </div>
              {hasReviews && (
                <div className="text-[13px] text-slate-500">
                  Showing <span className="font-semibold text-slate-900">{reviews.length}</span> · most recent first
                </div>
              )}
            </div>

            <div className="mt-6">
              <ReviewForm
                slug={coach.slug}
                onCreated={(r) => {
                  setReviews((prev) => [r, ...prev]);
                  setCoach((prev) => {
                    if (!prev) return prev;
                    const newCount = prev.review_count + 1;
                    const newAvg = ((prev.avg_rating * prev.review_count) + r.rating) / newCount;
                    return {
                      ...prev,
                      review_count: newCount,
                      avg_rating: Math.round(newAvg * 100) / 100,
                    };
                  });
                }}
              />
            </div>

            {hasReviews ? (
              <ul
                data-testid={COACH_PROFILE.reviewsList}
                className="mt-10 bg-white border border-slate-200 rounded-2xl overflow-hidden"
              >
                {reviews.map((r) => (
                  <ReviewItem
                    key={r.id}
                    review={r}
                    coachEmail={coach.contact_email}
                    onUpdated={(updated) =>
                      setReviews((prev) => prev.map((x) => (x.id === updated.id ? updated : x)))
                    }
                  />
                ))}
              </ul>
            ) : (
              <div
                data-testid={COACH_PROFILE.reviewsList}
                className="mt-10 border border-dashed border-slate-200 rounded-2xl p-10 text-center"
              >
                <div className="eyebrow">§ Empty</div>
                <h3 className="mt-3 font-display text-2xl text-slate-900">No reviews yet.</h3>
                <p className="mt-2 text-slate-500 max-w-[46ch] mx-auto">
                  Be the first to review {coach.name}. Reviews are only open to clients who've completed a session with them.
                </p>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <aside id="booking-widget" className="lg:sticky lg:top-24 h-fit space-y-6 scroll-mt-24">
            <BookingWidget coach={coach} />
            <div className="bg-slate-900 text-white rounded-2xl p-6">
              <div className="text-[11px] tracking-[0.24em] uppercase text-white/60 font-semibold">§ At a glance</div>
              <div className="mt-4 space-y-4">
                <div className="flex items-baseline justify-between">
                  <span className="text-white/60 text-[13px]">Avg. rating</span>
                  <span className="font-display text-3xl tracking-tight">
                    {coach.review_count > 0 ? coach.avg_rating.toFixed(1) : "—"}
                  </span>
                </div>
                <div className="flex items-baseline justify-between">
                  <span className="text-white/60 text-[13px]">Total reviews</span>
                  <span className="font-semibold text-lg">{coach.review_count}</span>
                </div>
                <div className="flex items-baseline justify-between">
                  <span className="text-white/60 text-[13px]">Response</span>
                  <span className="text-[#10B981] text-[13px] font-medium">Usually within 24h</span>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Session-at-a-glance: unambiguous mode / duration / price / timezone panel.
// Rendered inside the profile hero card so a client never has to hunt for
// "does this coach do video calls?" or "what timezone is this?".
// ---------------------------------------------------------------------------
function SessionAtAGlance({ coach }) {
  const online = coach.online_available;
  const inPerson = coach.in_person_available;
  const tz = coach.timezone || "UTC";
  const modes = coach.session_modes || (online && inPerson ? "both" : online ? "online" : inPerson ? "in_person" : "online");
  const modeLine =
    modes === "both"
      ? "Online or in-person"
      : modes === "online"
      ? "Online video calls only"
      : `In-person only · ${coach.city}`;
  const offset = tzOffsetLabel(tz);
  const openDays = (coach.weekly_availability || [])
    .slice()
    .sort((a, b) => a.weekday - b.weekday)
    .map((w) => WEEKDAY_SHORT[w.weekday]);

  return (
    <div
      data-testid="coach-session-at-a-glance"
      className="mt-10 pt-8 border-t border-slate-100"
    >
      <div className="flex items-center gap-2">
        <Users2 className="w-4 h-4 text-[#2563EB]" strokeWidth={2.4} />
        <div className="eyebrow">§ Session at a glance</div>
      </div>

      <dl className="mt-4 grid sm:grid-cols-2 md:grid-cols-4 gap-3">
        <GlanceTile
          testId="glance-mode"
          Icon={online && inPerson ? Users2 : online ? Video : MapPin}
          label="Session type"
          value={modeLine}
          extra={
            <div className="mt-2 flex gap-1.5 flex-wrap">
              {online && <ModeChip icon={Video} tone="blue" label="Online" />}
              {inPerson && <ModeChip icon={MapPin} tone="green" label={`In person · ${coach.city}`} />}
            </div>
          }
        />
        <GlanceTile
          testId="glance-duration"
          Icon={Clock}
          label="Length"
          value={`${coach.session_duration_minutes || 60}-minute session`}
        />
        <GlanceTile
          testId="glance-price"
          Icon={ShieldCheck}
          label="Price"
          value={<span>${coach.price_per_session || coach.hourly_rate} <span className="text-[13px] text-slate-500 font-normal">/ session</span></span>}
        />
        <GlanceTile
          testId="glance-timezone"
          Icon={Globe2}
          label="Coach's timezone"
          value={<span>{tz}{offset && <span className="ml-1 text-[12.5px] text-slate-500">({offset})</span>}</span>}
          extra={
            openDays.length > 0 ? (
              <p className="mt-2 text-[11.5px] text-slate-500">
                Typically open: <span className="text-slate-800 font-medium">{openDays.join(" · ")}</span>
              </p>
            ) : (
              <p className="mt-2 text-[11.5px] text-amber-600">No open days configured yet.</p>
            )
          }
        />
      </dl>
      <p className="mt-3 text-[12px] text-slate-400">
        Booking calendar shows times in your local timezone — no math needed.
      </p>
    </div>
  );
}

function GlanceTile({ Icon, label, value, extra, testId }) {
  return (
    <div data-testid={testId} className="rounded-2xl border border-slate-200 bg-white p-4">
      <div className="flex items-center gap-2 text-slate-500">
        <Icon className="w-4 h-4" strokeWidth={2} />
        <span className="text-[11px] tracking-[0.22em] uppercase font-semibold">{label}</span>
      </div>
      <div className="mt-2 text-[15px] font-semibold text-slate-900">{value}</div>
      {extra}
    </div>
  );
}

function ModeChip({ icon: Icon, tone, label }) {
  const toneCls =
    tone === "blue"
      ? "bg-[#2563EB]/8 text-[#2563EB]"
      : "bg-[#10B981]/12 text-[#059669]";
  return (
    <span className={`inline-flex items-center gap-1 h-6 px-2 rounded-full text-[11px] font-semibold ${toneCls}`}>
      <Icon className="w-3 h-3" strokeWidth={2.4} />
      {label}
    </span>
  );
}

// Convert a YouTube / Vimeo URL to its embed variant so <iframe> can render it.
// Unknown / already-embed URLs are returned as-is.
function _embedUrl(url = "") {
  const yt = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/))([\w-]{6,})/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vim = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vim) return `https://player.vimeo.com/video/${vim[1]}`;
  return url;
}
